import FooterSection from "assets/Footer";
import Navbar from "assets/Navbar";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaMinus, FaPlus, FaTrash } from "react-icons/fa";
import RevealOnScroll from "components/RevealOnScroll";

import { getCartUpdatedEventName, setCheckoutSelectedItems } from "utils/cart";
import ShimmerCart from "components/shimmer/ShimmerCart";
import ScrollToTopButton from "components/ScrollToTopButton";
import CartTopSection from "components/cartComponents/cartTopSection";

const AddCartDetails = () => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const toCurrency = (amount) => `${amount.toLocaleString()} TK`;
  const getItemKey = (item) => item.cartKey || `${String(item.item_id ?? "")}`;
  const steps = [
    { id: 1, label: "Add Items", state: "done" },
    { id: 2, label: "Review Cart", state: "current" },
    { id: 3, label: "Order Now", state: "pending" },
  ];
  const handleToggleSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === products.length) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(products.map((item) => getItemKey(item)));
  };

  const handleRemoveSelected = () => {
    if (!selectedIds.length) return;
    const nextItems = products.filter(
      (item) => !selectedIds.includes(getItemKey(item)),
    );
    updateLocalStorage(nextItems);
    setSelectedIds([]);
    window.location.reload();
    // showToast("Selected items removed");
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 200) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const storedItems = JSON.parse(localStorage.getItem("babyShop") || "[]");
      setProducts(storedItems);
      setLoading(false);
    }, 500);
  }, []);

  const updateLocalStorage = (items) => {
    localStorage.setItem("babyShop", JSON.stringify(items));
  };

  const increaseQuantity = (index) => {
    const updated = [...products];
    updated[index].quantity = (updated[index].quantity || 1) + 1;
    setProducts(updated);
    updateLocalStorage(updated);
  };

  const decreaseQuantity = (index) => {
    const updated = [...products];
    if ((updated[index].quantity || 1) > 1) {
      updated[index].quantity -= 1;
      setProducts(updated);
      updateLocalStorage(updated);
    }
  };

  const removeItem = (index) => {
    const updated = [...products];
    updated.splice(index, 1); // remove the item
    setProducts(updated);
    updateLocalStorage(updated);
    window.dispatchEvent(
      new CustomEvent(getCartUpdatedEventName(), {
        detail: {
          count: updated.reduce((sum, item) => sum + (item.quantity || 1), 0),
        },
      }),
    );
  };

  const moveToOrderNow = (ids = selectedIds) => {
    const fallbackIds = products.map((item) => getItemKey(item));
    const finalIds = Array.isArray(ids) && ids.length ? ids : fallbackIds;
    console.log("ADD TO CART - Selected IDs:", finalIds);

    setCheckoutSelectedItems(finalIds);

    navigate("/orderDetails", { state: { selectedIds: finalIds } });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto w-full">
        <RevealOnScroll>
          <CartTopSection
            steps={steps}
            subtitle="Step 2 of 3: Review products before placing order."
          />
        </RevealOnScroll>
        <div className="flex-1 mb-3">
          {loading ? (
            <ShimmerCart />
          ) : products.length === 0 ? (
            <RevealOnScroll delayMs={60}>
              <section className="rounded-2xl bg-white p-6 text-center shadow-sm ring-1 ring-slate-100 sm:p-10 mx-4 sm:mx-6">
                <h2 className="text-xl font-bold text-slate-900">
                  Cart is empty
                </h2>
                <p className="mt-2 text-sm text-slate-600 sm:text-base">
                  Add products to cart from the dashboard sections.
                </p>
                <button
                  type="button"
                  onClick={() => (window.location = "/")}
                  className="mt-5 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
                >
                  Continue Shopping
                </button>
              </section>
            </RevealOnScroll>
          ) : (
            <RevealOnScroll delayMs={60}>
              <div className="">
                {/* <section className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-100 sm:p-5 mx-4 sm:mx-6"> */}
                <section className="rounded-lg bg-white p-4 pb-5 sm:pb-5 shadow-sm ring-1 ring-slate-100 sm:p-5 mx-4 sm:mx-6">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === products.length}
                        onChange={handleSelectAll}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      Select all
                    </label>
                    <button
                      type="button"
                      onClick={handleRemoveSelected}
                      disabled={!selectedIds.length}
                      className="inline-flex items-center gap-2 rounded-md border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-600 transition disabled:cursor-not-allowed disabled:opacity-40 sm:text-sm"
                    >
                      <FaTrash />
                      Remove Selected
                    </button>
                  </div>

                  <div className="space-y-3">
                    {products.map((item, index) => {
                      const quantity = item.quantity || 1;

                      return (
                        <article
                          key={index}
                          className="overflow-hidden bg-white border border-slate-100 rounded-lg shadow-sm"
                        >
                          <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr]">
                            <div className="flex items-start gap-2 py-3 sm:border-r sm:border-slate-100 sm:py-4 px-2">
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(getItemKey(item))}
                                onChange={() =>
                                  handleToggleSelection(getItemKey(item))
                                }
                                className="mt-1 h-4 w-4 rounded border-slate-300"
                              />
                              <img
                                src={`https://backend.nuhababy.com/${item.selectedVariant.images[0]}`}
                                alt={item.selectedVariant?.title}
                                className="h-24 w-24 rounded-lg object-cover sm:h-28 sm:w-28"
                              />
                              <div className="sm:hidden grid gap-2 text-xs text-slate-600 sm:text-sm">
                                <p className="inline-flex items-center gap-2 rounded-md bg-slate-50 px-2 py-1.5">
                                  Price:
                                  <span className="font-semibold text-slate-900 text-sm">
                                    {toCurrency(
                                      item.selectedVariant?.flashPrice,
                                    )}
                                  </span>
                                  <span className="text-slate-500 line-through">
                                    {toCurrency(item.selectedVariant?.discount)}
                                  </span>
                                </p>

                                <p className="flex-1 sm:flex-none rounded-md bg-slate-50 px-2 py-1.5">
                                  Total:
                                  <span className="font-semibold text-slate-900 ml-1">
                                    {toCurrency(
                                      (item.selectedVariant?.flashPrice || 0) *
                                        (item.quantity || 1),
                                    )}
                                  </span>
                                </p>
                              </div>
                            </div>

                            <div className="sm:py-4 px-2">
                              <h3 className="text-sm text-slate-800 truncate">
                                {item.mainTitle}
                              </h3>
                              <h3 className="text-xs text-slate-800 truncate">
                                {item.selectedVariant?.title}
                              </h3>
                              <div className="hidden sm:grid sm:grid-cols-2 gap-2 text-xs text-slate-600 sm:text-sm pt-2">
                                <p className="inline-flex items-center gap-2 rounded-md bg-slate-50 px-2 py-1.5">
                                  Price:
                                  <span className="font-semibold text-slate-900">
                                    {toCurrency(
                                      item.selectedVariant?.flashPrice,
                                    )}
                                  </span>
                                  <span className="text-slate-500 line-through">
                                    {toCurrency(item.selectedVariant?.discount)}
                                  </span>
                                </p>

                                <p className="flex-1 sm:flex-none rounded-md bg-slate-50 px-2 py-1.5">
                                  Total:
                                  <span className="font-semibold text-slate-900 ml-1">
                                    {toCurrency(
                                      (item.selectedVariant?.flashPrice || 0) *
                                        (item.quantity || 1),
                                    )}
                                  </span>
                                </p>
                              </div>

                              <div className="my-3 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
                                <div className="inline-flex items-center rounded-md border border-slate-200 bg-white">
                                  <button
                                    type="button"
                                    disabled={quantity === 1}
                                    onClick={() => decreaseQuantity(index)}
                                    className="inline-flex h-8 w-8 items-center justify-center text-slate-700 transition bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                                    aria-label={`Decrease quantity of ${item.title}`}
                                  >
                                    <FaMinus className="text-xs" />
                                  </button>
                                  <span className="inline-flex min-w-8 items-center justify-center px-2 text-sm font-semibold text-slate-800">
                                    {item.quantity || 0}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => increaseQuantity(index)}
                                    className="inline-flex h-8 w-8 items-center justify-center text-slate-700 transition bg-slate-100"
                                    aria-label={`Increase quantity of ${item.title}`}
                                  >
                                    <FaPlus className="text-xs" />
                                  </button>
                                </div>

                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => removeItem(index)}
                                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 sm:text-sm"
                                  >
                                    <FaTrash />
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                  <div className="hidden sm:block sm:mt-5">
                    <button
                      type="button"
                      onClick={() => moveToOrderNow()}
                      className="w-full rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
                    >
                      Next Step: Order Now
                    </button>
                  </div>
                </section>
              </div>
            </RevealOnScroll>
          )}
        </div>

        {showScrollTop && <ScrollToTopButton />}
      </main>
      {products.length > 0 && (
        <div className="fixed bottom-12 left-0 w-full z-50 bg-white border-t border-slate-200 p-3 sm:hidden">
          <button
            type="button"
            onClick={() => moveToOrderNow()}
            className="w-full rounded-md bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            Next Step: Order Now
          </button>
        </div>
      )}
      <FooterSection />
    </div>
  );
};

export default AddCartDetails;
